import React, { useMemo, useState } from 'react';
import { AlertTriangleIcon, CalendarRangeIcon, CreditCardIcon } from 'lucide-react';
import { MetricCard } from './MetricCard';
import { StatusBadge } from './StatusBadge';
import { Customer, Payment, PaymentStatus, seededLifecycleEvents } from '../data/demoData';
type PaymentsPanelProps = {
  payments: Payment[];
  users: Customer[];
};
export function PaymentsPanel({
  payments,
  users
}: PaymentsPanelProps) {
  const [status, setStatus] = useState<PaymentStatus | 'All'>('All');
  const filteredPayments = useMemo(() => payments.filter(payment => status === 'All' || payment.status === status), [payments, status]);
  const accountMetrics = useMemo(() => calculateAccountMetrics(users, payments), [payments, users]);
  const nameFor = (id: string) => {
    const user = users.find(item => item.id === id);
    return user ? `${user.firstName} ${user.lastName}` : 'Unknown customer';
  };
  return <section aria-labelledby="payments-title" data-id="element-187" data-mp="2dw4h8ixcbt5s">
      <div className="border-b border-[#A6B6F8]/50 pb-6" data-id="element-188" data-mp="2dute8lbpypfq">
        <h1 id="payments-title" className="text-3xl font-bold tracking-tight text-[#041F60]" data-id="element-189" data-mp="28xt8kbis7op5">
          Payments
        </h1>
        <p className="mt-2 text-sm text-stone-600" data-id="element-190" data-mp="2mw9o8rymgpjf">
          Collection performance across all accounts.
        </p>
      </div>
      <div className="mt-5 flex items-stretch gap-3 overflow-x-auto pb-1" data-id="element-191" data-mp="1v5wlilvz04zm">
        <div className="min-w-[156px] flex-1" data-id="element-192" data-mp="1v3sjk9hmtg1z">
          <MetricCard label="Monthly recurring revenue" value="£98.00" data-id="element-193" data-mp="1q7la46q11mfa" />
        </div>
        <div className="min-w-[156px] flex-1" data-id="element-194" data-mp="23yqe1l0fh0vf">
          <MetricCard label="Free-to-paid conversion" value="64%" data-id="element-195" data-mp="23zrslzgnk5hw" />
        </div>
        <div className="min-w-[156px] flex-1" data-id="element-196" data-mp="1z6ot7ap721ts">
          <MetricCard label="Active subscriptions" value="2" data-id="element-197" data-mp="1bhw683qjv4bb" />
        </div>
        <div className="min-w-[156px] flex-1" data-id="element-198" data-mp="1bg30cqnrgo4d">
          <MetricCard label="Average duration" value={accountMetrics.averageDuration} data-id="element-199" data-mp="17gu5figzik78" />
        </div>
        <div className="min-w-[156px] flex-1" data-id="element-200" data-mp="36kj2zhxnpqlg">
          <MetricCard label="Lifetime value" value={accountMetrics.lifetimeValue} data-id="element-201" data-mp="3bjvpu2oj2xo1" />
        </div>
      </div>
      <section className="mt-5 overflow-hidden rounded-xl border border-[#A6B6F8]/50 bg-white" aria-labelledby="payment-list-title" data-id="element-202" data-mp="3gc6neyvsq7df">
        <div className="flex flex-col gap-4 border-b border-[#A6B6F8]/50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-203" data-mp="2nmg7qleimxf7">
          <div data-id="element-204" data-mp="2smccimv1esvt">
            <h2 id="payment-list-title" className="flex items-center gap-2 font-bold" data-id="element-205" data-mp="2slt54030jmw1">
              <CreditCardIcon size={18} className="text-[#5B5299]" data-id="element-206" data-mp="2xmqlq88dk8vf" /> All
              collections
            </h2>
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold" data-id="element-207" data-mp="cuojv3n23mit">
            <span className="sr-only" data-id="element-208" data-mp="ht85t41ngyb6">Payment status</span>
            <CalendarRangeIcon size={16} className="text-stone-500" data-id="element-209" data-mp="hron76siub68" />
            <select value={status} onChange={event => setStatus(event.target.value as PaymentStatus | 'All')} className="rounded-md border border-[#A6B6F8]/60 px-2.5 py-2 text-sm outline-none focus:ring-2 focus:ring-[#4BE4FF]" data-id="element-210" data-mp="22bk38vnszw8y">
              <option data-id="element-211" data-mp="1nll5uv42oouj">All</option>
              <option data-id="element-212" data-mp="1nka2uxigbl4h">Pending</option>
              <option data-id="element-213" data-mp="1int5rdt9xwf9">Submitted</option>
              <option data-id="element-214" data-mp="1iouqka03dxa6">Confirmed</option>
              <option data-id="element-215" data-mp="1elpt4luvclse">Paid out</option>
              <option data-id="element-216" data-mp="1ekn3eh9a8pe2">Failed</option>
            </select>
          </label>
        </div>
        <div className="hidden grid-cols-[1fr_1.6fr_.8fr_.8fr] gap-4 border-b border-[#A6B6F8]/50 bg-[#A6B6F8]/10 px-5 py-3 text-xs font-bold uppercase tracking-[.12em] text-stone-500 md:grid" data-id="element-217" data-mp="19ltnki6iwmaq">
          <span data-id="element-218" data-mp="340w6fx12h64o">Date</span>
          <span data-id="element-219" data-mp="3404agl3k56dv">Customer</span>
          <span data-id="element-220" data-mp="33ya1841nf2pm">Amount</span>
          <span data-id="element-221" data-mp="2z11h3xwxwolq">Status</span>
        </div>
        <div className="divide-y divide-stone-100" data-id="element-222" data-mp="2z22qzz27z51v">
          {filteredPayments.map(payment => <div key={payment.id} className="grid gap-2 px-5 py-4 text-sm md:grid-cols-[1fr_1.6fr_.8fr_.8fr] md:items-center md:gap-4" data-id="element-223" data-mp="1won4rq4k2ezo">
              <span className="font-semibold" data-id="element-224" data-mp="1mow8pk0cjrxh">
                {payment.date}
                <span className="ml-2 font-mono text-xs font-normal text-stone-400" data-id="element-225" data-mp="1roisydele8fs">
                  {payment.id}
                </span>
              </span>
              <span className="text-[#041F60]" data-id="element-226" data-mp="1cy1k48zmog8v">{nameFor(payment.userId)}</span>
              <span className="font-bold" data-id="element-227" data-mp="1hsfhggxuoxg6">£{payment.amount.toFixed(2)}</span>
              <span data-id="element-228" data-mp="140isumve55oj">
                <StatusBadge status={payment.status} data-id="element-229" data-mp="1410w43gh0k5y" />
              </span>
            </div>)}
          {!filteredPayments.length && <p className="px-5 py-10 text-center text-sm text-stone-500" data-id="element-230" data-mp="u1jxg5zmz6jh">
              No payments with this status.
            </p>}
        </div>
      </section>
      <p className="mt-4 flex items-start gap-2 text-xs leading-5 text-stone-500" data-id="element-231" data-mp="z27k6fgu117w">
        <AlertTriangleIcon size={15} className="mt-0.5 shrink-0 text-amber-600" data-id="element-232" data-mp="feuwdzpqklaz" />
        Direct Debit collection statuses may take several working days to
        update.
      </p>
    </section>;
}
function calculateAccountMetrics(users: Customer[], payments: Payment[]) {
  const reportingDate = new Date('2026-07-13T12:00:00');
  const signedUpByUserId = new Map(seededLifecycleEvents.filter(event => event.kind === 'signed_up').map(event => [event.userId, toDate(event.date)]));
  const cancelledByUserId = new Map(seededLifecycleEvents.filter(event => event.kind === 'cancelled').map(event => [event.userId, toDate(event.date)]));
  const membershipDays = users.map(user => {
    const signedUpAt = signedUpByUserId.get(user.id);
    if (!signedUpAt) {
      return null;
    }
    const endDate = cancelledByUserId.get(user.id) || reportingDate;
    return Math.max(0, Math.round((endDate.getTime() - signedUpAt.getTime()) / 86400000));
  }).filter((days): days is number => days !== null);
  const averageDays = membershipDays.length ? membershipDays.reduce((total, days) => total + days, 0) / membershipDays.length : 0;
  const collectedPayments = payments.filter(payment => payment.status === 'Paid out' || payment.status === 'Confirmed');
  const payingUserIds = new Set(collectedPayments.map(payment => payment.userId));
  const totalCollected = collectedPayments.reduce((total, payment) => total + payment.amount, 0);
  const lifetimeValue = payingUserIds.size ? totalCollected / payingUserIds.size : 0;
  return {
    averageDuration: `${(averageDays / 30.44).toFixed(1)} mo`,
    lifetimeValue: `£${lifetimeValue.toFixed(2)}`,
    payingMembers: payingUserIds.size
  };
}
function toDate(value: string) {
  return new Date(`${value} 12:00:00`);
}